import React, { useCallback, useEffect, useRef, useState } from 'react';
import { supabaseAdmin } from '../config/supabase';
import toast from 'react-hot-toast';
import { Wrench } from 'lucide-react';
import {
  TECHNICAL_BREAK_SETTING_KEY,
  buildDisabledTechnicalBreakValue,
  isTechnicalBreakTimedExpired,
  normalizeTechnicalBreakValue,
} from '../utils/technicalBreak';
import './TechnicalBreakConfig.css';

const SETTING_KEY = TECHNICAL_BREAK_SETTING_KEY;

const DEFAULT_FORM = {
  enabled: false,
  message: '',
  durationMode: 'indefinite',
  endsAt: '',
};

function toDatetimeLocalValue(isoString) {
  if (!isoString) return '';
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function fromDatetimeLocalValue(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

export default function TechnicalBreakConfig() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const autoDisableInFlightRef = useRef(false);

  const applyValueToForm = useCallback((value) => {
    setForm({
      enabled: value.enabled === true,
      message: typeof value.message === 'string' ? value.message : '',
      durationMode: value.durationMode === 'timed' ? 'timed' : 'indefinite',
      endsAt: toDatetimeLocalValue(value.endsAt),
    });
  }, []);

  const persistSettingValue = useCallback(async (settingValue) => {
    const { error } = await supabaseAdmin
      .from('app_settings')
      .upsert(
        {
          setting_key: SETTING_KEY,
          setting_value: settingValue,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'setting_key' }
      );
    if (error) throw error;
    return settingValue;
  }, []);

  const autoDisableIfExpired = useCallback(
    async (rawValue, { silent = false } = {}) => {
      if (!isTechnicalBreakTimedExpired(rawValue)) return false;
      if (autoDisableInFlightRef.current) return false;
      autoDisableInFlightRef.current = true;
      try {
        const disabled = buildDisabledTechnicalBreakValue(rawValue);
        await persistSettingValue(disabled);
        applyValueToForm(disabled);
        if (!silent) {
          toast.success('Przerwa techniczna zakończyła się — została automatycznie wyłączona');
        }
        return true;
      } catch (err) {
        console.error('Error auto-disabling technical break:', err);
        if (!silent) {
          toast.error('Nie udało się automatycznie wyłączyć przerwy technicznej');
        }
        return false;
      } finally {
        autoDisableInFlightRef.current = false;
      }
    },
    [applyValueToForm, persistSettingValue]
  );

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabaseAdmin
        .from('app_settings')
        .select('setting_value')
        .eq('setting_key', SETTING_KEY)
        .maybeSingle();

      if (error) throw error;

      const raw = data?.setting_value || {};
      if (isTechnicalBreakTimedExpired(raw)) {
        await autoDisableIfExpired(raw);
        return;
      }
      applyValueToForm(normalizeTechnicalBreakValue(raw));
    } catch (err) {
      console.error('Error loading technical break config:', err);
      toast.error('Błąd ładowania przerwy technicznej');
      setForm(DEFAULT_FORM);
    } finally {
      setLoading(false);
    }
  }, [applyValueToForm, autoDisableIfExpired]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    if (!form.enabled || form.durationMode !== 'timed' || !form.endsAt) return undefined;

    const endsAtIso = fromDatetimeLocalValue(form.endsAt);
    if (!endsAtIso) return undefined;
    const endsAtMs = new Date(endsAtIso).getTime();
    if (Number.isNaN(endsAtMs)) return undefined;

    const msUntilEnd = endsAtMs - Date.now();
    const fireAutoDisable = () => {
      autoDisableIfExpired({
        enabled: true,
        message: form.message,
        durationMode: 'timed',
        endsAt: endsAtIso,
      });
    };

    if (msUntilEnd <= 0) {
      fireAutoDisable();
      return undefined;
    }

    const timeoutId = setTimeout(fireAutoDisable, msUntilEnd);
    const intervalId = setInterval(() => {
      if (Date.now() >= endsAtMs) fireAutoDisable();
    }, 15000);

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [
    form.enabled,
    form.durationMode,
    form.endsAt,
    form.message,
    autoDisableIfExpired,
  ]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const message = (form.message || '').trim();
    if (form.enabled && !message) {
      toast.error('Wpisz komunikat wyświetlany w aplikacji mobilnej');
      return;
    }

    let endsAt = null;
    if (form.enabled && form.durationMode === 'timed') {
      endsAt = fromDatetimeLocalValue(form.endsAt);
      if (!endsAt) {
        toast.error('Wybierz datę i godzinę zakończenia przerwy');
        return;
      }
      if (new Date(endsAt).getTime() <= Date.now()) {
        toast.error('Koniec przerwy musi być w przyszłości');
        return;
      }
    }

    setSaving(true);
    try {
      const settingValue = {
        enabled: form.enabled === true,
        message,
        durationMode: form.enabled && form.durationMode === 'timed' ? 'timed' : 'indefinite',
        endsAt: form.enabled && form.durationMode === 'timed' ? endsAt : null,
      };

      await persistSettingValue(settingValue);
      toast.success(
        form.enabled ? 'Przerwa techniczna włączona' : 'Przerwa techniczna wyłączona'
      );
    } catch (err) {
      console.error('Error saving technical break config:', err);
      toast.error('Błąd zapisu przerwy technicznej');
    } finally {
      setSaving(false);
    }
  };

  const handleDisableNow = async () => {
    setSaving(true);
    try {
      const disabled = buildDisabledTechnicalBreakValue({
        message: (form.message || '').trim(),
      });
      await persistSettingValue(disabled);
      applyValueToForm(disabled);
      toast.success('Przerwa techniczna wyłączona');
    } catch (err) {
      console.error('Error disabling technical break:', err);
      toast.error('Błąd wyłączania przerwy technicznej');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="manage-tab-panel technical-break-panel" style={{ padding: '20px', textAlign: 'center' }}>
        <p style={{ color: '#000000' }}>Ładowanie ustawień przerwy technicznej...</p>
      </div>
    );
  }

  return (
    <div className="manage-tab-panel technical-break-panel" style={{ maxWidth: '640px' }}>
      <h1 style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '10px', color: '#000000' }}>
        <Wrench size={28} color="#000000" />
        Przerwa techniczna
      </h1>
      <p style={{ marginBottom: '24px', fontSize: '15px', color: '#000000' }}>
        Po włączeniu użytkownicy zobaczą w aplikacji mobilnej blokujący komunikat (logo ze splash screena i
        treść poniżej) zaraz po zakończeniu ekranu powitalnego. Aplikacja nie będzie nadawała się do
        użytkowania, dopóki przerwy nie wyłączysz lub nie minie ustawiony czas.
      </p>

      <form
        className="technical-break-form"
        onSubmit={handleSubmit}
        style={{
          backgroundColor: '#fff',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          padding: '20px',
          color: '#000000',
        }}
      >
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            cursor: 'pointer',
            marginBottom: '20px',
          }}
        >
          <input
            type="checkbox"
            checked={form.enabled}
            disabled={saving}
            onChange={(e) => handleChange('enabled', e.target.checked)}
            style={{ width: '20px', height: '20px', cursor: 'pointer' }}
          />
          <span style={{ fontWeight: '600', fontSize: '16px', color: '#000000' }}>Włącz przerwę techniczną</span>
        </label>

        {form.enabled && (
          <>
            <fieldset style={{ border: 'none', padding: 0, margin: '0 0 20px' }}>
              <legend style={{ fontWeight: '600', marginBottom: '10px', fontSize: '15px', color: '#000000' }}>
                Czas trwania
              </legend>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px', cursor: 'pointer', color: '#000000' }}>
                <input
                  type="radio"
                  name="durationMode"
                  value="indefinite"
                  checked={form.durationMode === 'indefinite'}
                  disabled={saving}
                  onChange={() => handleChange('durationMode', 'indefinite')}
                />
                <span style={{ color: '#000000' }}>Bez określonego czasu (do ręcznego wyłączenia)</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', color: '#000000' }}>
                <input
                  type="radio"
                  name="durationMode"
                  value="timed"
                  checked={form.durationMode === 'timed'}
                  disabled={saving}
                  onChange={() => handleChange('durationMode', 'timed')}
                />
                <span style={{ color: '#000000' }}>Do określonej daty i godziny</span>
              </label>
            </fieldset>

            {form.durationMode === 'timed' && (
              <div style={{ marginBottom: '20px' }}>
                <label htmlFor="technical-break-ends-at" style={{ display: 'block', fontWeight: '500', marginBottom: '8px', color: '#000000' }}>
                  Koniec przerwy
                </label>
                <input
                  id="technical-break-ends-at"
                  type="datetime-local"
                  className="form-input"
                  value={form.endsAt}
                  disabled={saving}
                  onChange={(e) => handleChange('endsAt', e.target.value)}
                  style={{ maxWidth: '280px', color: '#000000', backgroundColor: '#ffffff' }}
                />
              </div>
            )}

            <div style={{ marginBottom: '20px' }}>
              <label htmlFor="technical-break-message" style={{ display: 'block', fontWeight: '500', marginBottom: '8px', color: '#000000' }}>
                Komunikat dla użytkowników
              </label>
              <textarea
                id="technical-break-message"
                className="form-input"
                rows={6}
                value={form.message}
                disabled={saving}
                onChange={(e) => handleChange('message', e.target.value)}
                placeholder="Np. Trwa przerwa techniczna. Prosimy spróbować później."
                style={{ width: '100%', resize: 'vertical', minHeight: '120px', color: '#000000', backgroundColor: '#ffffff' }}
              />
            </div>
          </>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Zapisywanie...' : 'Zapisz'}
          </button>
          {form.enabled && (
            <button
              type="button"
              className="btn btn-secondary"
              disabled={saving}
              onClick={handleDisableNow}
            >
              Wyłącz przerwę teraz
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
